import { useState, useCallback } from "react";
import bidService from "../services/bidService";
import analyticsService from "../services/analyticsService";

/**
 * Hook personalizado para gestionar el proceso de reserva/redención
 * @returns {Object} Estado y funciones de la reserva
 */
export const useReservation = () => {
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState(null);
  const [reservationUrl, setReservationUrl] = useState(null);

  /**
   * Procesa la solicitud de redención de noches
   * @param {Object} data - Datos de la reserva
   * @param {Object} data.user - Información del usuario
   * @param {Object} data.offer - Información de la oferta
   * @param {Object} data.formData - Datos del formulario
   * @param {string} data.tipo - Tipo de oferta
   */
  const submitReservation = useCallback(async (data) => {
    const { user, offer, formData, tipo } = data;

    try {
      setStatus("loading");
      setError(null);

      const reservationData = {
        user,
        oferta: offer,
        tipo,
        ...formData,
      };

      const response = await bidService.redeemNights(reservationData);

      // Registrar evento en Analytics
      analyticsService.trackRedemptionSuccess({
        userName: user.afiliado1.titular,
        offerTitle: offer.titulo,
      });

      setReservationUrl(response);
      setStatus("success");

      // Abrir URL en nueva pestaña
      if (response && typeof response === "string") {
        window.open(response, "_blank", "noopener noreferrer");
      }

      return response;
    } catch (err) {
      const errorMessage = err.message || "Hubo un error con la solicitud";
      setError(errorMessage);
      setStatus("error");

      // Registrar error en Analytics
      analyticsService.trackError({
        errorMessage,
        context: "Redención de noches",
      });

      throw err;
    }
  }, []);

  /**
   * Reinicia el estado de la reserva
   */
  const reset = useCallback(() => {
    setStatus("idle");
    setError(null);
    setReservationUrl(null);
  }, []);

  /**
   * Reintenta la solicitud
   */
  const retry = useCallback(
    async (data) => {
      reset();
      return submitReservation(data);
    },
    [reset, submitReservation]
  );

  return {
    status,
    error,
    reservationUrl,
    submitReservation,
    reset,
    retry,
    isLoading: status === "loading",
    isSuccess: status === "success",
    isError: status === "error",
    isIdle: status === "idle",
  };
};
